import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, getDoc  } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase'; // Ensure auth is imported correctly

const AppContext = createContext();

// Custom hook to use the context
export const useAppContext = () => {
  return useContext(AppContext);
};

// Context provider component
export const AppProvider = ({ children }) => {
  const [sacraments, setSacraments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null); // Store user's Firestore data

  const refreshSacraments = () => {
  // This triggers the Firestore listener by re-setting the userData
  // (You may need a more robust way to refresh if you're only using onSnapshot)
  setUserData((u) => ({ ...u })); // force update (hacky)
  // If you want a direct fetch (bypassing snapshot), you’d write it with getDocs instead
};


  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      console.log("🟢 Auth state changed, currentUser:", currentUser);
      setUser(currentUser);
  
      if (!currentUser) {
        console.warn("⚠️ No authenticated user, clearing user data.");
        setUserData(null);
        setSacraments([]); // Clear data when user logs out
      }
    });
  
    return () => unsubscribeAuth(); // Cleanup on unmount
  }, []);

  useEffect(() => {
    if (user === null) {
      console.warn("⚠️ No authenticated user, skipping user data fetch.");
      return;
    }
  
    if (!user.uid) {
      console.warn("⚠️ User is authenticated, but UID is missing. Waiting...");
      return;
    }
  
    console.log("🟢 Fetching user data for UID:", user.uid);
  
    const userDocRef = doc(db, "users", user.uid);
  
    getDoc(userDocRef)
      .then((docSnap) => {
        if (docSnap.exists()) {
          console.log("✅ User document exists! Fetching data...");
          setUserData(docSnap.data());
        } else {
          console.warn("⚠️ No user data found in Firestore for UID:", user.uid);
          setUserData(null);
        }
      })
      .catch((error) => {
        console.error("❌ Firestore Query Error:", error.code, error.message);
      });
  
  }, [user]); // ✅ Runs only after `user` is fully available
  

  useEffect(() => {
    console.log("🔍 Debugging userData:", userData);
    if (!userData || !userData.parishId) {
      console.warn("⚠️ User data not available or missing parishId, skipping sacraments fetch.");
      return;
    }
    

    console.log("DEBUG: userData before fetching sacraments: ", userData);
    console.log("DEBUG: parishId before fetching sacraments: ", userData?.parishId);


    const parishId = userData.parishId;
    const year = 2025;
    console.log(`🟢 Fetching sacraments for Parish ID: ${parishId} and Year: ${year}`);

    const q = query(collection(db, "sacraments"), where("parishId", "==", parishId), where("year", "==", year));

    const unsubscribeSacraments = onSnapshot(
      q,
      (querySnapshot) => {
        console.log(`🔥 Firestore Query Executed for Parish ID: ${parishId}`);

        if (!querySnapshot.empty) {
          setSacraments(querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
          console.log(`✅ Loaded ${querySnapshot.docs.length} sacraments`);
        } else {
          console.warn(`⚠️ No sacraments found for parish ${parishId}`);
          setSacraments([]);
        }
      },
      (error) => {
        console.error("❌ Firestore Query Error:", error);
      }
    );

    return () => unsubscribeSacraments();
  }, [userData]);

  return (
    <AppContext.Provider value={{ sacraments, loading, setLoading, error, setError, refreshSacraments   }}>
      {children}
    </AppContext.Provider>
  );
};
